import { useEffect, useState } from 'react';
import { notification } from 'antd';

import ThirdPartyIntegration from 'services/thirdPartyIntegrationService';
import { getThirdPartyIdFromStationIntegrationList } from '../helpers/thirdPartyIntegrationHelper';

const parseJsonSafe = (value) => {
  try {
    return JSON.parse(value || '{}');
  } catch (error) {
    return {};
  }
};

export default function useThirdPartyConfigForm({
  id,
  setId,
  form,
  translation,
  thirdPartyCode,
  mapFromPartyRequiredData,
  buildTestPayload,
  buildUpdatePayload,
  testApi,
  updateApi,
  testSuccessMessageKey = 'vietqr.successMessage',
  testErrorMessageKey = 'vietqr.errorMessage',
  updateSuccessMessageKey = 'tingee.updateSuccess',
  updateErrorMessageKey = 'tingee.updateError',
}) {
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [hasFormChanged, setHasFormChanged] = useState(false);
  const [showConfirmTest, setShowConfirmTest] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  useEffect(() => {
    if (id) {
      handleGetData(id);
    }
  }, [id]);

  const getComparableFormValues = (values = {}) => {
    if (!initialFormValues) return {};
    const comparable = {};
    Object.keys(initialFormValues).forEach((key) => {
      comparable[key] = values[key] ?? '';
    });
    return comparable;
  };

  const handleSetupData = (res = {}) => {
    const partyRequiredData = parseJsonSafe(res?.partyRequiredData);
    const connected = res?.partyActiveStatus === 1;
    const normalizedValues = mapFromPartyRequiredData(partyRequiredData, res);

    form.setFieldsValue({
      ...normalizedValues,
      isConnected: connected,
      isEnable: normalizedValues.isEnable,
    });

    setInitialFormValues(normalizedValues);
    setHasFormChanged(false);
    setIsConnected(connected);
  };

  const handleGetData = async (targetId = id) => {
    if (!targetId) return;
    try {
      setLoading(true);
      const res = await ThirdPartyIntegration.getThirdPartyById({ id: targetId });
      handleSetupData(res);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConfig = async (valuesArg) => {
    try {
      const values = valuesArg || form.getFieldsValue(true);
      const payload = buildTestPayload(values);
      const result = await testApi(payload);
      if (result) {
        setIsConnected(true);
        form.setFieldsValue({ isConnected: true });
        notification.success({ message: '', description: translation(testSuccessMessageKey) });
        return true;
      }
      return false;
    } catch (error) {
      notification.error({ message: '', description: translation(testErrorMessageKey) });
      return false;
    }
  };

  const handleUpdateConfig = async (values, forceConnected = false) => {
    try {
      setLoading(true);
      const payload = buildUpdatePayload(values, forceConnected || isConnected);
      await updateApi(payload);

      const updatedThirdPartyId = await getThirdPartyIdFromStationIntegrationList(thirdPartyCode);
      if (!updatedThirdPartyId) {
        throw new Error(`${thirdPartyCode}_ID_NOT_FOUND_FROM_LIST`);
      }

      if (Number(updatedThirdPartyId) !== Number(id)) {
        setId(updatedThirdPartyId);
      } else {
        await handleGetData(updatedThirdPartyId);
      }

      notification.success({ message: '', description: translation(updateSuccessMessageKey) });
      setHasFormChanged(false);
    } catch (error) {
      notification.error({ message: '', description: translation(updateErrorMessageKey) });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!isConnected) {
      setPendingValues(values);
      setShowConfirmTest(true);
      return;
    }
    await handleUpdateConfig(values);
  };

  const handleConfirmAndSave = async () => {
    const values = pendingValues || form.getFieldsValue(true);
    const tested = await handleTestConfig(values);
    if (!tested) return;
    setShowConfirmTest(false);
    await handleUpdateConfig(values, true);
  };

  const handleValuesChange = () => {
    if (!initialFormValues) return;
    const currentValues = getComparableFormValues(form.getFieldsValue(true));
    setHasFormChanged(JSON.stringify(currentValues) !== JSON.stringify(initialFormValues));
  };

  return {
    loading,
    isConnected,
    hasFormChanged,
    showConfirmTest,
    setShowConfirmTest,
    handleSubmit,
    handleConfirmAndSave,
    handleValuesChange,
  };
}
